import { useState } from "react";
import {
  SECTION_META,
  EXAM_CONFIG,
  PRACTICE_CONFIG,
  QUESTION_BANKS,
  MBTA_SECTIONS,
  EXTRA_SECTIONS,
} from "../data/index";
import StudyGuide from "./StudyGuide";
import ReadinessCard from "./ReadinessCard";

const MBTA_DRILL_CATEGORIES = [
  { key: "gk", label: "General Knowledge", icon: "🚛", color: "#1a56db", lightColor: "#eff6ff", borderColor: "#bfdbfe", count: 71, time: 90 },
  { key: "ab", label: "Air Brakes",        icon: "⚙️", color: "#7c3aed", lightColor: "#f5f3ff", borderColor: "#ddd6fe", count: 40, time: 45 },
  { key: "pa", label: "Passenger",         icon: "🚌", color: "#0891b2", lightColor: "#ecfeff", borderColor: "#a5f3fc", count: 35, time: 35 },
];

export default function HomeScreen({ onStartPractice, onStartExam, onStartMbtaTraining, onStartWeakDrill, onGoHistory, onGoGuide, onGoStudyPlanning, onGoAirBrakeInspector, onGoAirBrakeSkillsTest, onGoCDLTutorial, onGoPreTripInspection }) {
  const [activeTab, setActiveTab] = useState("home");

  return (
    <div className="home-screen">
      <header className="home-header">
        <div className="home-logo">🚌</div>
        <h1>CDL Exam Prep</h1>
        <p className="home-subtitle">Class B · Passenger Endorsement · Air Brakes</p>
      </header>

      <div className="tab-bar">
        <button className={`tab-btn ${activeTab === "home"     ? "active" : ""}`} onClick={() => setActiveTab("home")}>🏠 Home</button>
        <button className={`tab-btn ${activeTab === "practice" ? "active" : ""}`} onClick={() => setActiveTab("practice")}>📚 Practice</button>
        <button className={`tab-btn ${activeTab === "exam"     ? "active" : ""}`} onClick={() => setActiveTab("exam")}>🎯 Exam</button>
        <button className={`tab-btn ${activeTab === "guide"    ? "active" : ""}`} onClick={onGoGuide}>📘 Full Guide</button>
        <button className={`tab-btn ${activeTab === "pretrip" ? "active" : ""}`} onClick={onGoPreTripInspection}>🔍 Pre-Trip</button>
        <button className={`tab-btn ${activeTab === "planning"  ? "active" : ""}`} onClick={onGoStudyPlanning}>📅 Planning</button>
        <button className={`tab-btn ${activeTab === "history"  ? "active" : ""}`} onClick={onGoHistory}>📜 History</button>
      </div>

      {activeTab === "home"      && <HomeTab onGoPractice={() => setActiveTab("practice")} onGoExam={() => setActiveTab("exam")} onGoHistory={onGoHistory} onStartMbtaTraining={onStartMbtaTraining} onStartWeakDrill={onStartWeakDrill} />}
      {activeTab === "practice" && <PracticeTab onStart={onStartPractice} onStartMbtaTraining={onStartMbtaTraining} />}
      {activeTab === "exam"     && <ExamTab onStart={onStartExam} />}
    </div>
  );
}

function HomeTab({ onGoPractice, onGoExam, onStartMbtaTraining, onStartWeakDrill, onGoInspector }) {
  const mbtaTotal = MBTA_SECTIONS.reduce((t, s) => t + QUESTION_BANKS[s].length, 0);
  const extraTotal = EXTRA_SECTIONS.reduce((t, s) => t + QUESTION_BANKS[s].length, 0);

  return (
    <div className="home-tab">
      {/* What you need banner */}
      <div className="mbta-banner">
        <h2>What you need to pass</h2>
        <div className="mbta-requirements">
          <div className="req-item">
            <span className="req-num">①</span>
            <div>
              <strong>General Knowledge</strong>
              <small>50 questions · 80% to pass · 60 min</small>
            </div>
          </div>
          <div className="req-item">
            <span className="req-num">②</span>
            <div>
              <strong>Air Brakes</strong>
              <small>25 questions · 80% to pass · 30 min</small>
            </div>
          </div>
          <div className="req-item">
            <span className="req-num">③</span>
            <div>
              <strong>Passenger (P) Endorsement</strong>
              <small>20 questions · 80% to pass · 25 min</small>
            </div>
          </div>
          <div className="req-item">
            <span className="req-num">④</span>
            <div>
              <strong>CDL Skills Test</strong>
              <small>Pre-trip · Basic controls · On-road driving</small>
            </div>
          </div>
        </div>
      </div>

      {/* Readiness Dashboard */}
      <ReadinessCard onStartWeakDrill={onStartWeakDrill} />

      {/* Study path CTA */}
      {/* Practical Skills Training */}
      <div className="practical-skills-banner">
        <div className="ps-icon">🔧</div>
        <div className="ps-text">
          <strong>Ready to practice on the road?</strong>
          <p>Master bus pre-trip inspections, air brake systems, and safe operation procedures with interactive training.</p>
        </div>
        <button className="btn btn-practical" onClick={onGoInspector}>Air Brake Inspector Training →</button>
      </div>

      {/* MBTA Training Drills — 3 category cards */}
      <div className="mbta-drills-section">
        <div className="mbta-drills-header">
          <span className="mbta-training-badge">MBTA DRILLS</span>
          <h3>Practice by Category</h3>
          <p>Questions sourced directly from MBTA Day 1 &amp; Day 2 training quiz sheets.</p>
        </div>
        <div className="mbta-drill-cards">
          {MBTA_DRILL_CATEGORIES.map((cat) => (
            <div
              key={cat.key}
              className="mbta-drill-card"
              style={{ borderColor: cat.borderColor, background: cat.lightColor }}
              onClick={() => onStartMbtaTraining(cat.key)}
            >
              <span className="mbta-drill-icon">{cat.icon}</span>
              <div className="mbta-drill-info">
                <strong style={{ color: cat.color }}>{cat.label}</strong>
                <small>{cat.count} questions · {cat.time} min</small>
              </div>
              <button
                className="btn btn-mbta-training"
                style={{ background: cat.color }}
                onClick={(e) => { e.stopPropagation(); onStartMbtaTraining(cat.key); }}
              >
                Start →
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Mode cards */}
      <div className="mode-cards">
        <div className="mode-card practice-card">
          <div className="mode-icon">📚</div>
          <h2>Practice Mode</h2>
          <p>Study all questions at your own pace. Get instant feedback and explanations after every answer.</p>
          <ul className="mode-features">
            <li>✓ All questions from the full bank</li>
            <li>✓ Randomized every session</li>
            <li>✓ Instant answer explanations</li>
            <li>✓ Flag &amp; review questions</li>
            <li>✓ No time pressure</li>
          </ul>
          <button className="btn btn-practice" onClick={onGoPractice}>Start Practice →</button>
        </div>
        <div className="mode-card exam-card">
          <div className="mode-icon">🎯</div>
          <h2>Exam Mode</h2>
          <p>Simulate the real CDL written exam. Random questions, strict time limit, no hints until the end.</p>
          <ul className="mode-features">
            <li>✓ 50 GK / 25 Air Brakes / 20 Passenger</li>
            <li>✓ New random questions every attempt</li>
            <li>✓ Timed like the real test</li>
            <li>✓ No feedback until submission</li>
            <li>✓ Full results &amp; review after</li>
          </ul>
          <button className="btn btn-exam" onClick={onGoExam}>Start Exam →</button>
        </div>
      </div>

      {/* Question bank stats */}
      <div className="bank-stats">
        <h3>MBTA Required — Question Bank</h3>
        <div className="stats-row">
          {MBTA_SECTIONS.map((s) => {
            const meta = SECTION_META[s];
            return (
              <div key={s} className="stat-card" style={{ borderColor: meta.borderColor, background: meta.lightColor }}>
                <span className="stat-icon">{meta.icon}</span>
                <span className="stat-count" style={{ color: meta.color }}>{meta.bankSize}</span>
                <span className="stat-label">{s}</span>
              </div>
            );
          })}
          <div className="stat-card total-stat">
            <span className="stat-icon">📋</span>
            <span className="stat-count">{mbtaTotal}</span>
            <span className="stat-label">MBTA Total</span>
          </div>
        </div>

        <h3 style={{ marginTop: "18px" }}>Extra Endorsements — Additional Practice</h3>
        <div className="stats-row">
          {EXTRA_SECTIONS.map((s) => {
            const meta = SECTION_META[s];
            return (
              <div key={s} className="stat-card" style={{ borderColor: meta.borderColor, background: meta.lightColor, opacity: 0.8 }}>
                <span className="stat-icon">{meta.icon}</span>
                <span className="stat-count" style={{ color: meta.color }}>{meta.bankSize}</span>
                <span className="stat-label">{s}</span>
              </div>
            );
          })}
          <div className="stat-card total-stat">
            <span className="stat-icon">📋</span>
            <span className="stat-count">{mbtaTotal + extraTotal}</span>
            <span className="stat-label">All Questions</span>
          </div>
        </div>
      </div>

      <div className="tips-box">
        <h3>📌 CDL Exam Tips — Pass First Try</h3>
        <ul>
          <li>You need <strong>80%</strong> to pass each knowledge test (General, Air Brakes, Passenger).</li>
          <li><strong>Study Guide first →</strong> memorize key numbers (PSI values, time limits, distances).</li>
          <li><strong>Practice Mode</strong> until you score 85%+ consistently on every section.</li>
          <li><strong>Exam Mode</strong> to simulate real test conditions before your appointment.</li>
          <li>Use <strong>Drill Weak Spots</strong> to focus only on questions you keep missing.</li>
          <li>Take your skills test in a vehicle WITH air brakes to avoid the "L" restriction.</li>
          <li>Bring your DOT medical certificate to every RMV appointment.</li>
        </ul>
      </div>
    </div>
  );
}

function PracticeTab({ onStart, onStartMbtaTraining }) {
  return (
    <div className="mode-tab">
      <h2 className="tab-title">📚 Choose a Section to Practice</h2>

      {/* MBTA Training Drills — 3 category cards */}
      <div className="mbta-drills-section">
        <div className="mbta-drills-header">
          <span className="mbta-training-badge">MBTA DRILLS</span>
          <h3>Practice by Category</h3>
          <p>Questions sourced directly from MBTA Day 1 &amp; Day 2 training quiz sheets.</p>
        </div>
        <div className="mbta-drill-cards">
          {MBTA_DRILL_CATEGORIES.map((cat) => (
            <div
              key={cat.key}
              className="mbta-drill-card"
              style={{ borderColor: cat.borderColor, background: cat.lightColor }}
              onClick={() => onStartMbtaTraining(cat.key)}
            >
              <span className="mbta-drill-icon">{cat.icon}</span>
              <div className="mbta-drill-info">
                <strong style={{ color: cat.color }}>{cat.label}</strong>
                <small>{cat.count} questions · {cat.time} min</small>
              </div>
              <button
                className="btn btn-mbta-training"
                style={{ background: cat.color }}
                onClick={(e) => { e.stopPropagation(); onStartMbtaTraining(cat.key); }}
              >
                Start →
              </button>
            </div>
          ))}
        </div>
      </div>

      <p className="tab-section-label required-label">✅ MBTA Required — Pass all three to get your Class B CDL</p>
      <div className="section-cards">
        {MBTA_SECTIONS.map((section) => {
          const meta = SECTION_META[section];
          const cfg = PRACTICE_CONFIG[section];
          return (
            <div className="section-card required-section" key={section} style={{ borderColor: meta.borderColor }}>
              <div className="section-card-top" style={{ background: meta.lightColor }}>
                <span className="section-icon">{meta.icon}</span>
                <div>
                  <h3 style={{ color: meta.color }}>{section}</h3>
                  <span className="section-tag">{meta.bankSize} questions in bank</span>
                </div>
              </div>
              <div className="section-topics">
                {meta.topics.map((t) => <span key={t} className="topic-chip">{t}</span>)}
              </div>
              <div className="section-meta-row">
                <span>⏱ {cfg.timeMinutes} min</span>
                <span>✅ Pass: 80%</span>
                <span>📋 All {meta.bankSize} Qs</span>
              </div>
              <button
                className="btn btn-section-start"
                style={{ background: meta.color }}
                onClick={() => onStart(section, cfg.timeMinutes)}
              >
                Practice {section} →
              </button>
            </div>
          );
        })}
      </div>

      <p className="tab-section-label extra-label">📘 Additional Endorsements — Not required for MBTA Class B</p>
      <div className="section-cards">
        {EXTRA_SECTIONS.map((section) => {
          const meta = SECTION_META[section];
          const cfg = PRACTICE_CONFIG[section];
          return (
            <div className="section-card extra-section" key={section} style={{ borderColor: meta.borderColor }}>
              <div className="section-card-top" style={{ background: meta.lightColor }}>
                <span className="section-icon">{meta.icon}</span>
                <div>
                  <h3 style={{ color: meta.color }}>{section}</h3>
                  <span className="section-tag">{meta.bankSize} questions in bank</span>
                </div>
              </div>
              <div className="section-topics">
                {meta.topics.map((t) => <span key={t} className="topic-chip">{t}</span>)}
              </div>
              <div className="section-meta-row">
                <span>⏱ {cfg.timeMinutes} min</span>
                <span>✅ Pass: 80%</span>
                <span>📋 All {meta.bankSize} Qs</span>
              </div>
              <button
                className="btn btn-section-start"
                style={{ background: meta.color }}
                onClick={() => onStart(section, cfg.timeMinutes)}
              >
                Practice {section} →
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ExamTab({ onStart }) {
  return (
    <div className="mode-tab">
      <h2 className="tab-title">🎯 CDL Exam Simulation</h2>
      <p className="tab-desc">Simulate the real written CDL test. Random questions each time. No feedback until you submit.</p>

      <div className="exam-overview-card">
        <h3>📋 MBTA Class B Exam Structure</h3>
        <div className="exam-structure">
          {MBTA_SECTIONS.map((section) => {
            const meta = SECTION_META[section];
            const cfg = EXAM_CONFIG[section];
            return (
              <div key={section} className="exam-row" style={{ borderColor: meta.borderColor, background: meta.lightColor }}>
                <span className="exam-row-icon">{meta.icon}</span>
                <span className="exam-row-name" style={{ color: meta.color }}>{section}</span>
                <span className="exam-row-count"><strong>{cfg.count}</strong> questions</span>
                <span className="exam-row-time">⏱ {cfg.timeMinutes} min</span>
                <span className="exam-row-bank">from {SECTION_META[section].bankSize} in bank</span>
              </div>
            );
          })}
        </div>
        <p className="exam-note">⚠️ Questions are randomly drawn and shuffled each attempt — no two exams are alike.</p>
      </div>

      <div className="exam-section-header">
        <span className="exam-section-badge">MBTA REQUIRED</span>
        <h3>Take a Practice Exam</h3>
        <p>Choose a section or take the full exam to test your knowledge under real conditions.</p>
      </div>
      <div className="exam-start-cards">
        {MBTA_SECTIONS.map((section) => {
          const meta = SECTION_META[section];
          const cfg = EXAM_CONFIG[section];
          return (
            <div key={section} className="exam-start-card" style={{ borderColor: meta.borderColor }}>
              <span style={{ fontSize: "2rem" }}>{meta.icon}</span>
              <div>
                <strong style={{ color: meta.color }}>{section}</strong>
                <br />
                <small>{cfg.count} random Qs · {cfg.timeMinutes} min · 80% to pass</small>
              </div>
              <button
                className="btn btn-exam-start"
                style={{ background: meta.color }}
                onClick={() => onStart(section, cfg.count, cfg.timeMinutes)}
              >
                Start
              </button>
            </div>
          );
        })}
      </div>

      <details className="exam-extra-endorsements">
        <summary>
          <span className="exam-extra-badge">📘 EXTRA ENDORSEMENTS</span>
          <span className="exam-extra-title">Additional Practice Exams</span>
          <span className="exam-extra-toggle">▼</span>
        </summary>
        <div className="exam-start-cards" style={{ marginTop: "12px" }}>
          {EXTRA_SECTIONS.map((section) => {
            const meta = SECTION_META[section];
            const cfg = EXAM_CONFIG[section];
            return (
              <div key={section} className="exam-start-card" style={{ borderColor: meta.borderColor, opacity: 0.85 }}>
                <span style={{ fontSize: "2rem" }}>{meta.icon}</span>
                <div>
                  <strong style={{ color: meta.color }}>{section}</strong>
                  <br />
                  <small>{cfg.count} random Qs · {cfg.timeMinutes} min · 80% to pass</small>
                </div>
                <button
                  className="btn btn-exam-start"
                  style={{ background: meta.color }}
                  onClick={() => onStart(section, cfg.count, cfg.timeMinutes)}
                >
                  Start
                </button>
              </div>
            );
          })}
        </div>
      </details>
    </div>
  );
}

function InspectorTab({ onStartInspector, onStartSkillsTest }) {
  return (
    <div className="mode-tab">
      <h2 className="tab-title">🔧 Practical Skills Training</h2>
      <p className="tab-subtitle">Now that you've passed the written exam, let's prepare you for safe bus operation on the road.</p>

      <div className="inspector-cards">
        <div className="inspector-card featured">
          <div className="inspector-card-header">
            <span className="inspector-icon">🎓</span>
            <h3>CDL Air Brake Skills Test</h3>
            <span className="featured-badge">OFFICIAL TEST SIMULATOR</span>
          </div>
          <p>
            Practice the official 11-step air brake inspection procedure from your CDL exam. This is the exact test you'll perform
            with the examiner during your road test. Includes realistic pressure gauges and timing requirements.
          </p>
          <div className="inspector-features">
            <h4>All 11 Steps:</h4>
            <ul>
              <li>✓ Securing the bus & building air pressure</li>
              <li>✓ Governor cut-in and cut-out (PSI readings)</li>
              <li>✓ Air leakage rate tests (timed)</li>
              <li>✓ Low air warning and spring brake tests</li>
              <li>✓ Pressure build-up rate test (45 seconds)</li>
              <li>✓ Parking brake test (on road)</li>
              <li>✓ Service brake test (5 mph driving test)</li>
            </ul>
          </div>
          <div className="inspector-modes">
            <span className="mode-badge">📊 Interactive Gauges</span>
            <span className="mode-badge">⏱️ Realistic Timers</span>
            <span className="mode-badge">✓ Pass/Fail Scoring</span>
          </div>
          <button className="btn btn-inspector-start featured-btn" onClick={onStartSkillsTest}>
            Start Skills Test Simulator →
          </button>
        </div>

        <div className="inspector-card">
          <div className="inspector-card-header">
            <span className="inspector-icon">📋</span>
            <h3>Air Brake System Inspection</h3>
          </div>
          <p>
            Master the critical pre-trip inspection procedures for air brake systems. Learn to identify components,
            understand safety readings, and perform all required checks before operating a bus.
          </p>
          <div className="inspector-features">
            <h4>What You'll Learn:</h4>
            <ul>
              <li>✓ Governor cutoff pressure readings (100-120 PSI)</li>
              <li>✓ Air leakage detection and limits</li>
              <li>✓ Parking and service brake operation</li>
              <li>✓ Articulated bus connection checks</li>
              <li>✓ Pre-trip inspection procedures</li>
              <li>✓ Safety tips and common mistakes</li>
            </ul>
          </div>
          <div className="inspector-modes">
            <span className="mode-badge">📋 Guided Tour</span>
            <span className="mode-badge">⏱️ Practice Mode</span>
            <span className="mode-badge">❓ Quiz Mode</span>
          </div>
          <button className="btn btn-inspector-start" onClick={onStartInspector}>
            Start Air Brake Inspector →
          </button>
        </div>

        <div className="inspector-card coming-soon">
          <div className="inspector-card-header">
            <span className="inspector-icon">🚌</span>
            <h3>Bus Operation & Backing</h3>
          </div>
          <p>
            Learn backing techniques, passenger loading procedures, and emergency maneuvers. Interactive scenarios
            with step-by-step guidance.
          </p>
          <span className="coming-soon-badge">Coming Soon</span>
        </div>

        <div className="inspector-card coming-soon">
          <div className="inspector-card-header">
            <span className="inspector-icon">🛑</span>
            <h3>Emergency Procedures</h3>
          </div>
          <p>
            Practice responding to brake failures, passenger medical emergencies, and other critical situations
            you might face on the road.
          </p>
          <span className="coming-soon-badge">Coming Soon</span>
        </div>
      </div>

      <div className="inspector-note">
        <h3>💡 How to Use This Training</h3>
        <ol>
          <li><strong>Guided Tour:</strong> Start here to learn each step with detailed explanations and safety tips.</li>
          <li><strong>Practice Mode:</strong> Complete a full inspection with a timer to simulate real-world conditions.</li>
          <li><strong>Quiz Mode:</strong> Test your knowledge with scenario-based questions and troubleshooting challenges.</li>
          <li><strong>Apply Your Skills:</strong> Use this knowledge during your actual bus operator training and road test.</li>
        </ol>
      </div>
    </div>
  );
}
